import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useLocale } from 'containers/App/LocaleContext';
import { refreshExtensionLogsCommand } from 'pages/Extensions/model/commands/refreshExtensionLogs';
import { useCommandModel } from 'utils/hooks/useCommandModel';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';
import { useApi } from 'utils/request/useApi';
import {
  type ExtensionsModel,
  createInitialExtensionsState,
} from '../model/ExtensionsModel';
import { hideExtensionDetailsDialog } from '../model/commands/hideExtensionDetailsDialog';

export const useExtensionsPage = (): {
  actions: {
    closeExtensionDetailsDialog: () => void;
    refreshExtensionLogs: () => void;
  };
  model: ExtensionsModel;
  permissions: {
    canRegisterExtension: boolean;
    canFetchExtensions: boolean;
  };
} => {
  const msg = useLocale();
  const apiScopeRequirementMet = useApiScopeRequirementMet();

  const canFetchExtensions = apiScopeRequirementMet({
    method: 'GET',
    path: '/extensions',
  });
  const canRegisterExtension =
    apiScopeRequirementMet({
      method: 'POST',
      path: '/extensions/docker-zip',
    }) ||
    apiScopeRequirementMet({
      method: 'POST',
      path: '/extensions/docker-image',
    });

  const model = useCommandModel(
    createInitialExtensionsState,
    {
      api: useApi(),
      showNotification: useNotifications().showNotification,
      msg,
    },
    {
      msg,
    },
  );

  const closeExtensionDetailsDialog = useStableCallback(() => {
    model.dispatch(hideExtensionDetailsDialog, {});
  });

  const refreshExtensionLogs = () => {
    model.dispatch(refreshExtensionLogsCommand, {
      extensionId: model.extensionDetails!.extension!.id,
    });
  };

  return {
    actions: {
      closeExtensionDetailsDialog,
      refreshExtensionLogs,
    },
    model,
    permissions: {
      canRegisterExtension,
      canFetchExtensions,
    },
  };
};
